import compare from 'es/app/business-logic/compare';
import Site from 'es/app/business-logic/domain-models/Site';
import {
    ISiteEntitySearchResult,
    ISiteEntitySearchResults,
    universalSiteEntitySearch,
} from 'es/app/business-logic/services/universal-search/SiteEntitySearch';
import { IUniversalSearchCriteria } from 'es/app/business-logic/services/universal-search/UniversalSearch';
import {
    UniversalSearchMatch,
    UniversalSearchResults,
} from 'es/app/components/layout/universal-search/UniversalSearchResults';
import { useUserStore } from 'es/app/store/GlobalStore';
import { useI18n } from 'es/core/hooks/useI18n';
import usePromiseTracker from 'es/core/hooks/usePromiseTracker';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react';
import React, { useEffect } from 'react';
import { usePromise } from 'react-use';

interface ISearchProvider {
    criteria: IUniversalSearchCriteria;
}

const SiteEntitySearchResult = (props: {
    isBusy: boolean;
    provider: string;
    entities: ISiteEntitySearchResult[] | undefined;
}) => {
    if (!props.entities?.length) return null;

    return (
        <UniversalSearchResults provider={props.provider}>
            {props.entities.map(entity => (
                <UniversalSearchMatch key={entity.id} heading={entity.label} match={entity} route={entity.route}>
                    {entity.description}
                </UniversalSearchMatch>
            ))}
        </UniversalSearchResults>
    );
};

class SiteEntitySearchStore implements ISiteEntitySearchResults {
    constructor(sites: Site[]) {
        this._sites = sites;
        makeObservable<SiteEntitySearchStore, '_index' | 'merge'>(this, {
            _index: observable,
            currentSite: computed,
            processes: observable,
            processVariables: observable,
            processSchemas: observable,
            searchCurrentSite: action.bound,
            merge: action.bound,
            resetSearch: action.bound,
            isEmpty: computed,
        });
    }

    private readonly _sites: Site[];

    private _index = 0;

    public get currentSite() {
        return this._sites[this._index] || null;
    }

    public processes?: ISiteEntitySearchResult[] = [];
    public processVariables?: ISiteEntitySearchResult[] = [];
    public processSchemas?: ISiteEntitySearchResult[] = [];

    public async searchCurrentSite(criteria: IUniversalSearchCriteria) {
        if (!this.currentSite) return;
        const result = await universalSiteEntitySearch(criteria, this.currentSite);
        this.merge(result);
        this._index++;
    }

    private merge(result: ISiteEntitySearchResults) {
        Object.keys(result).forEach(key => {
            const k = key as keyof ISiteEntitySearchResults;
            const current = this[k];
            const res = result[k];
            if (!!current) {
                if (!res) return;
                current.push(...res);
            } else {
                this[k] = res;
            }
        });
    }

    public resetSearch = () => {
        this.processes = [];
        this.processVariables = [];
        this._index = 0;
    };

    public get isEmpty() {
        return (this.processes?.length ?? 0) + (this.processVariables?.length ?? 0) === 0;
    }
}

const SiteEntitySearch = observer((props: ISearchProvider) => {
    const { l10n } = useI18n('app/components/layout', 'universalSearch');
    const { criteria } = props;

    const resolveIfMounted = usePromise();
    const { isBusy, trackPromise } = usePromiseTracker();

    const store = useStore();

    // Start searching sites from the beginning when the criteria changes
    useEffect(() => {
        store.resetSearch();
    }, [store, criteria]);

    // Search the current site when the criteria changes or the current site changes
    useEffect(() => {
        if (!store.currentSite) return;
        if (!(criteria.id || criteria.text)) return;
        const { abandon } = trackPromise(() => resolveIfMounted(store.searchCurrentSite(criteria)));
        return abandon;
    }, [criteria, resolveIfMounted, store, store.currentSite, trackPromise]);

    if (!isBusy && store.isEmpty) return null;

    return (
        <>
            {isBusy && store.currentSite && (
                <h6>{l10n('activity.searchingSite', { site: store.currentSite.toString() })}</h6>
            )}
            <SiteEntitySearchResult
                provider={l10n('site.processes.providerName')}
                isBusy={isBusy}
                entities={store?.processes}
            />
            <SiteEntitySearchResult
                provider={l10n('site.processVariables.providerName')}
                isBusy={isBusy}
                entities={store?.processVariables}
            />
        </>
    );

    function useStore() {
        const { accessibleFacilities, activeSite } = useUserStore();

        return useLocalObservable(() => {
            const compareByAuthDomain = compare.byStringValue<Site>(site => site.toString());

            const sites =
                accessibleFacilities
                    ?.map(facility => facility.site)
                    .sort((left, right) => {
                        // Search the currently active site first if there is one.
                        if (activeSite) {
                            if (left === activeSite) return -1;
                            if (right === activeSite) return 1;
                        }

                        return compareByAuthDomain(left, right);
                    }) ?? [];

            return new SiteEntitySearchStore(sites);
        });
    }
});

export default SiteEntitySearch;
